import React, { useState, useEffect, useRef, useCallback } from 'react'
import cx from 'classnames'
import { useAppSelector } from 'reduxTyped'
import { Popover } from '@manychat/manyui'

import { getBotTimezone } from 'common/core/selectors/appSelectors'
import { Field } from 'common/fields/entities/field'
import { useFilterContext } from 'common/filter/components/Filters/FilterContext'
import { useSegments } from 'common/filter/hooks/useSegments'
import { FilterCondition } from 'common/filter/interfaces'
import { UNARY_OPERATORS } from 'common/filter/models/AudienceFilter/constants'
import formatFilterCondition from 'common/filter/models/AudienceFilter/formatters'
import { getActiveReasons } from 'common/oneTimeNotify/selectors/notifyReasonSelectors'

import { ConditionEditor } from './components/ConditionEditor'
import { ConditionSummary } from './components/ConditionSummary'
import { FieldPopover } from './components/FieldPopover'
import { RemoveButton } from './components/RemoveButton'
import { UNKNOWN_OPERATOR, REMOVE_TIMEOUT } from './constants'
import { useConditionOperators } from './hooks/useConditionOperators'
import { useConditionPopovers } from './hooks/useConditionPopovers'
import { useConditionValidation } from './hooks/useConditionValidation'
import { ConditionProps } from './interfaces'

import cm from './Condition.module.css'

export const Condition = ({
  condition: conditionProp,
  className,
  summaryClassName,
  groupIndex,
  disabled = false,
}: ConditionProps) => {
  const { isBuilder, fields, config, removeCondition, changeCondition } = useFilterContext()
  const [removed, setRemoved] = useState(false)
  const [condition, setCondition] = useState(conditionProp)

  const timerRef = useRef<NodeJS.Timeout>()

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [])

  const handleRemoveClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault()
      event.stopPropagation()

      timerRef.current = setTimeout(() => {
        if (condition._oid) {
          removeCondition({
            groupIndex,
            _oid: condition._oid,
          })
        }
      }, REMOVE_TIMEOUT)
      setRemoved(true)
    },
    [removeCondition, groupIndex, condition._oid],
  )

  const handleConditionSave = useCallback(
    (conditionToSave: FilterCondition) => {
      if (conditionToSave._oid) {
        changeCondition({
          groupIndex,
          _oid: conditionToSave._oid,
          condition: conditionToSave,
        })
      }
    },
    [changeCondition, groupIndex],
  )

  const {
    valuePopoverAnchor,
    fieldPopoverAnchor,
    setValuePopoverAnchor,
    onFieldPopoverClose,
    onValuePopoverClose,
    onFieldLinkClick,
    onValueLinkClick,
    onFieldSelect,
    onValueRefChange,
  } = useConditionPopovers({
    condition,
    setCondition,
    saveCondition: handleConditionSave,
    isBuilder,
  })

  const handleConditionChange = useCallback(
    (data: Partial<FilterCondition>, closePopover?: boolean) => {
      const updatedCondition = { ...condition, ...data }
      setCondition(updatedCondition)
      if (closePopover) {
        setValuePopoverAnchor(null)
        handleConditionSave(updatedCondition)
      }
    },
    [condition, handleConditionSave, setValuePopoverAnchor],
  )
  const renderError = (error: string) => {
    return (
      <div
        className={cx(cm.root, className, {
          [cm.disabled]: disabled,
          [cm.removed]: removed,
        })}
      >
        <ConditionSummary
          valueRef={onValueRefChange}
          disabled={disabled}
          className={summaryClassName}
          error={error}
          onFieldLinkClick={onFieldLinkClick}
        />
        {!disabled && (
          <>
            {config.allowRemove && <RemoveButton onClick={handleRemoveClick} />}
            <FieldPopover
              onRequestClose={onFieldPopoverClose}
              isOpen={fieldPopoverAnchor != null}
              anchor={fieldPopoverAnchor}
              onSelect={onFieldSelect}
              fields={fields}
              isBuilder={isBuilder}
            />
          </>
        )}
      </div>
    )
  }

  const fieldDesc = fields.find((field) => field.id === condition.field)

  const { processedCondition, operatorDesc } = useConditionOperators(condition, fieldDesc)
  const { byId: segments } = useSegments()
  const botTimezone = useAppSelector(getBotTimezone)
  const activeReasons = useAppSelector(getActiveReasons)
  const error = useConditionValidation({
    condition: processedCondition,
    segments,
    activeReasons,
    fieldDesc,
  })

  if (error) {
    return renderError(error)
  }

  const [, operator, formattedValue] = formatFilterCondition(
    processedCondition.value,
    fieldDesc as Field,
    operatorDesc ?? UNKNOWN_OPERATOR,
    segments,
    {
      useChangedLabelForOperators: config.useChangedLabelForOperators,
      botTimezone: botTimezone ?? null,
    },
  )

  return (
    <div
      className={cx(cm.root, className, {
        [cm.disabled]: disabled,
        [cm.removed]: removed,
      })}
    >
      <ConditionSummary
        valueRef={onValueRefChange}
        icon={fieldDesc?.segmentType === 'cuf' ? fieldDesc.type : ''}
        caption={fieldDesc?.caption}
        operator={operator}
        value={formattedValue}
        ignoreValue={operatorDesc?.ignoreValue}
        validValue={
          formattedValue !== ''
            ? Boolean(`${processedCondition.value}` || operatorDesc?.ignoreValue)
            : operatorDesc && UNARY_OPERATORS.includes(operatorDesc.value)
        }
        className={summaryClassName}
        disabled={disabled}
        field={processedCondition.field}
        onFieldLinkClick={onFieldLinkClick}
        onValueLinkClick={onValueLinkClick}
      />
      {!disabled && fieldDesc && (
        <>
          {config.allowRemove && <RemoveButton onClick={handleRemoveClick} />}
          <FieldPopover
            onRequestClose={onFieldPopoverClose}
            isOpen={fieldPopoverAnchor != null}
            anchor={fieldPopoverAnchor}
            onSelect={onFieldSelect}
            fields={fields}
            isBuilder={isBuilder}
          />
          <Popover
            open={valuePopoverAnchor != null}
            anchor={valuePopoverAnchor}
            autoPosition="auto"
            anchorOrigin="bottom left"
            popoverOrigin="top left"
            onRequestClose={onValuePopoverClose}
          >
            <ConditionEditor
              condition={processedCondition}
              operatorDesc={operatorDesc ?? UNKNOWN_OPERATOR}
              fieldDesc={fieldDesc}
              onChange={handleConditionChange}
              onRequestClose={onValuePopoverClose}
              useChangedLabelForOperators={config.useChangedLabelForOperators}
              allowDateFormula={config.allowDateFormula}
              isBuilder={isBuilder}
            />
          </Popover>
        </>
      )}
    </div>
  )
}
